import { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, X } from 'lucide-react';
import clsx from 'clsx';
import { canvasToBlob, downloadBlob, downloadText, formatFilename, type RasterFormat } from '../../core/export/download';
import { renderDocument } from '../../core/export/renderDocument';
import { documentToSvg } from '../../core/export/svg';
import { TEXT_TECHNIQUE_ID } from '../../store/layer';
import { useStudio } from '../../store/studioStore';
import { getTechnique } from '../../techniques/registry';
import { Segmented, Row } from '../controls/Basics';
import { Slider } from '../controls/Slider';

type Format = RasterFormat | 'svg';

const DEFAULT_TEMPLATE = '{word}_{technique}_{seed}_{size}';

export function ExportDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const doc = useStudio((s) => s.doc);
  const font = useStudio((s) => s.font);

  const [format, setFormat] = useState<Format>('png');
  const [scale, setScale] = useState(doc.canvas.exportScale);
  const [quality, setQuality] = useState(0.92);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const width = Math.round(doc.canvas.width * scale);
  const height = Math.round(doc.canvas.height * scale);

  const tokens = useMemo(() => {
    const word = String(doc.textValues.text ?? 'text').split('\n')[0].slice(0, 24) || 'text';
    const techniqueLayer = doc.layers.find((l) => l.techniqueId !== TEXT_TECHNIQUE_ID && l.visible);
    const technique = techniqueLayer ? (getTechnique(techniqueLayer.techniqueId)?.id ?? 'text') : 'text';
    return { word, technique, seed: doc.seed, size: `${width}x${height}`, index: 1 };
  }, [doc, width, height]);

  const filename = `${formatFilename(template, tokens)}.${format === 'jpeg' ? 'jpg' : format}`;

  // 300 dpi is the print reference; showing the physical size makes the scale slider
  // meaningful for anyone exporting for paper rather than for a screen.
  const printCm = `${((width / 300) * 2.54).toFixed(1)} × ${((height / 300) * 2.54).toFixed(1)} cm`;

  const svgUnsupported = useMemo(
    () =>
      doc.layers
        .filter((l) => l.visible && l.techniqueId !== TEXT_TECHNIQUE_ID)
        .map((l) => getTechnique(l.techniqueId))
        .filter((t) => t && !t.toSVG)
        .map((t) => t?.name ?? '')
        .filter(Boolean),
    [doc.layers],
  );

  const run = async (): Promise<void> => {
    if (!font) return;
    setBusy(true);
    setMessage(null);
    setWarning(null);
    // Yield once so the spinner paints before the render blocks the main thread.
    await new Promise((r) => setTimeout(r, 20));

    try {
      const rendered = renderDocument({ doc, font, scale });

      if (format === 'svg') {
        const { svg, rasterOnly } = documentToSvg(doc, rendered, scale);
        downloadText(svg, filename, 'image/svg+xml');
        setMessage(`${filename} — ${(svg.length / 1024).toFixed(0)} kB, ${rendered.ms.toFixed(0)} ms`);
        if (rasterOnly.length) {
          setWarning(`Ohne Vektorform und daher nicht enthalten: ${[...new Set(rasterOnly)].join(', ')}.`);
        }
      } else {
        const blob = await canvasToBlob(rendered.canvas, format, quality);
        downloadBlob(blob, filename);
        setMessage(`${filename} — ${(blob.size / 1024 / 1024).toFixed(2)} MB, ${rendered.ms.toFixed(0)} ms`);
        if (format === 'jpeg' && doc.background.kind === 'transparent') {
          setWarning('JPEG kennt keine Transparenz — der Hintergrund wurde schwarz gefüllt.');
        }
      }
    } catch (err) {
      setWarning((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-6" onClick={onClose}>
      <div
        className="w-[420px] rounded-xl border border-ink-700 bg-ink-900 shadow-2xl shadow-black/70"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-3">
          <span className="flex-1 text-[13px] font-medium text-ink-100">Exportieren</span>
          <button type="button" className="ghost-button h-6 w-6 p-0" onClick={onClose}>
            <X size={13} />
          </button>
        </div>

        <div className="space-y-1 px-4 py-3">
          <Row label="Format">
            <Segmented
              size="sm"
              value={format}
              onChange={setFormat}
              options={[
                { value: 'png', label: 'PNG', title: 'Verlustfrei, mit Transparenz' },
                { value: 'jpeg', label: 'JPG', title: 'Klein, ohne Transparenz' },
                { value: 'webp', label: 'WebP' },
                { value: 'svg', label: 'SVG', title: 'Vektor, nur für vektorfähige Techniken' },
              ]}
            />
          </Row>

          <Slider
            label="Skalierung"
            value={scale}
            onChange={(v) => setScale(Math.round(v))}
            min={1}
            max={6}
            step={1}
            defaultValue={2}
            unit="×"
            precision={0}
            tooltip="Vielfaches der Dokumentgrösse. Längenparameter skalieren automatisch mit."
          />

          {(format === 'jpeg' || format === 'webp') && (
            <Slider label="Qualität" value={quality} onChange={setQuality} min={0.3} max={1} step={0.01} defaultValue={0.92} />
          )}

          <Row label="Dateiname">
            <input
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              spellCheck={false}
              className="field-input font-mono text-[11px]"
            />
          </Row>

          <div className="rounded-md bg-ink-850 px-2.5 py-2 font-mono text-[10px] leading-relaxed text-ink-400">
            <div className="truncate text-ink-200">{filename}</div>
            <div className="text-ink-600">
              {width} × {height} px · {printCm} bei 300 dpi
            </div>
            <div className="text-ink-600">Platzhalter: {'{word} {technique} {seed} {size} {index}'}</div>
          </div>

          {format === 'svg' && svgUnsupported.length > 0 && (
            <p className="rounded-md border border-amber-900/50 bg-amber-950/40 px-2.5 py-2 text-[10px] leading-relaxed text-amber-200">
              {[...new Set(svgUnsupported)].join(', ')} {svgUnsupported.length === 1 ? 'hat' : 'haben'} keine
              Vektorform und {svgUnsupported.length === 1 ? 'fehlt' : 'fehlen'} in der SVG-Datei. Für diese Layer
              PNG verwenden.
            </p>
          )}

          {message && (
            <p className="rounded-md bg-ink-850 px-2.5 py-2 font-mono text-[10px] text-accent-500">{message}</p>
          )}
          {warning && (
            <p className="rounded-md border border-amber-900/50 bg-amber-950/40 px-2.5 py-2 text-[10px] text-amber-200">
              {warning}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-ink-800 px-4 py-3">
          <span className="flex-1 text-[10px] text-ink-500">
            Der Export rendert unabhängig von der Vorschau in voller Qualität.
          </span>
          <button
            type="button"
            disabled={busy || !font}
            onClick={() => void run()}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
              busy || !font ? 'bg-ink-750 text-ink-500' : 'bg-accent-500 text-ink-950 hover:bg-accent-400',
            )}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            {busy ? 'Rendert…' : 'Exportieren'}
          </button>
        </div>
      </div>
    </div>
  );
}
